import { useEffect, useRef, useState } from "react";
import { Leaf, Recycle, Wind, Droplets } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

// Demo-data / industry-typical values, clearly labeled as ISO 14001 audit basis.
// Marco can replace with real audit numbers later.
const REDUCTION_BY_YEAR = [
  { year: 2020, value: 100 },
  { year: 2021, value: 92 },
  { year: 2022, value: 84 },
  { year: 2023, value: 76 },
  { year: 2024, value: 70 },
  { year: 2025, value: 66 },
];

const COPY = {
  de: {
    overline: "— Nachhaltigkeit · ISO 14001",
    title_1: "Schweizer Verantwortung,",
    title_2: "messbar dokumentiert.",
    intro: "Galvanik bedeutet Chemie und Energie. Wir arbeiten seit Jahren systematisch daran, beide effizient einzusetzen — und veröffentlichen die Resultate.",
    cards: [
      { id: "co2", icon: Leaf, value: "12.4", unit: "Tonnen", label: "CO₂-Äquivalent eingespart 2025", suffix_aria: "tonnes CO₂" },
      { id: "water", icon: Droplets, value: "92", unit: "%", label: "Prozesswasser im geschlossenen Kreislauf", suffix_aria: "percent" },
      { id: "recycling", icon: Recycle, value: "98", unit: "%", label: "Schwermetall-Recovery aus Bädern", suffix_aria: "percent" },
      { id: "energy", icon: Wind, value: "100", unit: "%", label: "Schweizer Strom (Mix mit Wasserkraft)", suffix_aria: "percent" },
    ],
    chart_label: "CO₂-Reduktion seit 2020",
    chart_unit: "Index 2020 = 100",
    minus_label: "Reduktion gesamt",
    note: "Werte gemäss ISO 14001 Umweltmanagement-Audit. Methodik & Vollbericht auf Anfrage.",
  },
  fr: {
    overline: "— Durabilité · ISO 14001",
    title_1: "Responsabilité suisse,",
    title_2: "documentée et mesurable.",
    intro: "La galvanoplastie, c'est de la chimie et de l'énergie. Nous travaillons systématiquement depuis des années à utiliser les deux efficacement — et publions les résultats.",
    cards: [
      { id: "co2", icon: Leaf, value: "12.4", unit: "tonnes", label: "Équivalent CO₂ économisé en 2025", suffix_aria: "tonnes de CO₂" },
      { id: "water", icon: Droplets, value: "92", unit: "%", label: "Eau de process en circuit fermé", suffix_aria: "pourcent" },
      { id: "recycling", icon: Recycle, value: "98", unit: "%", label: "Récupération des métaux lourds dans les bains", suffix_aria: "pourcent" },
      { id: "energy", icon: Wind, value: "100", unit: "%", label: "Électricité suisse (mix avec hydroélectrique)", suffix_aria: "pourcent" },
    ],
    chart_label: "Réduction CO₂ depuis 2020",
    chart_unit: "Indice 2020 = 100",
    minus_label: "Réduction totale",
    note: "Valeurs selon l'audit environnemental ISO 14001. Méthodologie et rapport complet sur demande.",
  },
  en: {
    overline: "— Sustainability · ISO 14001",
    title_1: "Swiss responsibility,",
    title_2: "documented and measurable.",
    intro: "Electroplating is chemistry and energy. For years we've been systematically working to use both efficiently — and we publish the results.",
    cards: [
      { id: "co2", icon: Leaf, value: "12.4", unit: "tonnes", label: "CO₂-equivalent saved in 2025", suffix_aria: "tonnes CO₂" },
      { id: "water", icon: Droplets, value: "92", unit: "%", label: "Process water in closed-loop", suffix_aria: "percent" },
      { id: "recycling", icon: Recycle, value: "98", unit: "%", label: "Heavy-metal recovery from baths", suffix_aria: "percent" },
      { id: "energy", icon: Wind, value: "100", unit: "%", label: "Swiss electricity (mix with hydropower)", suffix_aria: "percent" },
    ],
    chart_label: "CO₂ reduction since 2020",
    chart_unit: "Index 2020 = 100",
    minus_label: "Total reduction",
    note: "Values per ISO 14001 environmental management audit. Methodology and full report on request.",
  },
  it: {
    overline: "— Sostenibilità · ISO 14001",
    title_1: "Responsabilità svizzera,",
    title_2: "documentata e misurabile.",
    intro: "La galvanica significa chimica ed energia. Da anni lavoriamo sistematicamente per impiegare entrambe in modo efficiente — e pubblichiamo i risultati.",
    cards: [
      { id: "co2", icon: Leaf, value: "12.4", unit: "tonnellate", label: "Equivalente CO₂ risparmiato nel 2025", suffix_aria: "tonnellate CO₂" },
      { id: "water", icon: Droplets, value: "92", unit: "%", label: "Acqua di processo in circuito chiuso", suffix_aria: "percento" },
      { id: "recycling", icon: Recycle, value: "98", unit: "%", label: "Recupero metalli pesanti dai bagni", suffix_aria: "percento" },
      { id: "energy", icon: Wind, value: "100", unit: "%", label: "Elettricità svizzera (mix con idroelettrico)", suffix_aria: "percento" },
    ],
    chart_label: "Riduzione CO₂ dal 2020",
    chart_unit: "Indice 2020 = 100",
    minus_label: "Riduzione totale",
    note: "Valori secondo audit ambientale ISO 14001. Metodologia e rapporto completo su richiesta.",
  },
};

const Counter = ({ to, decimals = 0, duration = 1400 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(parseFloat(to) * eased);
              if (t < 1) requestAnimationFrame(tick);
              else setVal(parseFloat(to));
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);

  const display = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();
  return <span ref={ref}>{display}</span>;
};

export const CO2Dashboard = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const reduction = Math.round(100 - REDUCTION_BY_YEAR[REDUCTION_BY_YEAR.length - 1].value);

  return (
    <section
      id="nachhaltigkeit"
      data-testid="co2-section"
      className="relative bg-[#0B0B0D] py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 right-1/2 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.04] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5">{c.overline}</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[60px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-px">
          {c.cards.map((card, i) => {
            const Icon = card.icon;
            const decimals = card.value.includes(".") ? 1 : 0;
            return (
              <div
                key={card.id}
                data-testid={`co2-card-${card.id}`}
                className={`reveal reveal-delay-${(i % 4) + 1} bg-[#0E0E10] hover:bg-[#141416] transition-colors duration-500 p-6 md:p-8`}
              >
                <Icon className="w-5 h-5 text-emerald-400 mb-5" strokeWidth={1.5} />
                <div className="flex items-baseline gap-1.5 mb-3">
                  <span className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-white tabular-nums">
                    <Counter to={card.value} decimals={decimals} />
                  </span>
                  <span className="text-zinc-500 text-sm">{card.unit}</span>
                </div>
                <div className="text-[12px] text-zinc-400 leading-relaxed">{card.label}</div>
              </div>
            );
          })}
        </div>

        {/* Chart card */}
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10 border border-white/10 border-t-0">
          <div className="md:col-span-4 bg-[#0E0E10] p-6 md:p-8 flex flex-col justify-between min-h-[280px]">
            <div>
              <div className="overline mb-4">{c.chart_label}</div>
              <div className="font-display text-6xl md:text-7xl font-semibold tracking-tight text-white">
                <span className="text-emerald-400">−</span>
                <Counter to={reduction.toString()} />
                <span className="text-zinc-500 text-3xl md:text-4xl">%</span>
              </div>
              <div className="mt-3 text-[12px] text-zinc-400">{c.minus_label}</div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mt-6">
              {c.chart_unit}
            </div>
          </div>

          <div className="md:col-span-8 bg-[#0E0E10] p-6 md:p-8">
            <div className="relative h-[200px] md:h-[240px]">
              <svg viewBox="0 0 600 220" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="co2-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(52,211,153,0.4)" />
                    <stop offset="100%" stopColor="rgba(52,211,153,0)" />
                  </linearGradient>
                </defs>
                {/* Grid */}
                {[0, 50, 100].map((y) => (
                  <line key={y} x1="40" x2="600" y1={20 + (1 - y / 100) * 180} y2={20 + (1 - y / 100) * 180}
                        stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />
                ))}
                {/* Area */}
                <path
                  d={
                    REDUCTION_BY_YEAR.map((d, i) => {
                      const x = 40 + (i / (REDUCTION_BY_YEAR.length - 1)) * 540;
                      const y = 20 + (1 - d.value / 100) * 180;
                      return `${i === 0 ? "M" : "L"}${x},${y}`;
                    }).join(" ") + ` L600,200 L40,200 Z`
                  }
                  fill="url(#co2-grad)"
                />
                {/* Line */}
                <path
                  d={REDUCTION_BY_YEAR.map((d, i) => {
                    const x = 40 + (i / (REDUCTION_BY_YEAR.length - 1)) * 540;
                    const y = 20 + (1 - d.value / 100) * 180;
                    return `${i === 0 ? "M" : "L"}${x},${y}`;
                  }).join(" ")}
                  stroke="rgb(52,211,153)" strokeWidth="2" fill="none" strokeLinejoin="round"
                />
                {/* Dots */}
                {REDUCTION_BY_YEAR.map((d, i) => {
                  const x = 40 + (i / (REDUCTION_BY_YEAR.length - 1)) * 540;
                  const y = 20 + (1 - d.value / 100) * 180;
                  return <circle key={d.year} cx={x} cy={y} r="3.5" fill="#0E0E10" stroke="rgb(52,211,153)" strokeWidth="2" />;
                })}
                {/* Labels */}
                {REDUCTION_BY_YEAR.map((d, i) => {
                  const x = 40 + (i / (REDUCTION_BY_YEAR.length - 1)) * 540;
                  return (
                    <text key={d.year} x={x} y="216" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.4)" fontFamily="Inter, sans-serif" letterSpacing="1.5">
                      {d.year}
                    </text>
                  );
                })}
                <text x="32" y="28" textAnchor="end" fontSize="10" fill="rgba(255,255,255,0.3)" fontFamily="Inter, sans-serif">100</text>
                <text x="32" y="120" textAnchor="end" fontSize="10" fill="rgba(255,255,255,0.3)" fontFamily="Inter, sans-serif">50</text>
                <text x="32" y="205" textAnchor="end" fontSize="10" fill="rgba(255,255,255,0.3)" fontFamily="Inter, sans-serif">0</text>
              </svg>
            </div>
          </div>
        </div>

        <p className="mt-8 text-[12px] text-zinc-500 max-w-2xl leading-relaxed">{c.note}</p>
      </div>
    </section>
  );
};

export default CO2Dashboard;
